import React, { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";

import CardModal from "../dashboard/newTradeFlow/CardModal";
import ExpandAdd from "../dashboard/newTradeFlow/ExpandAdd";
import GiftcardRateFlow from "../dashboard/newTradeFlow/GiftcardRateFlow";
import UploadCardImage from "../dashboard/UploadCardImage";
import {
  useGiftCardDetailQuery,
  useGiftCardsListQuery,
  useInvalidateGiftCardCatalog,
} from "../../lib/api/useGiftCardCatalogQueries";

/**
 * Gift card catalog admin: pick/add cards, upload thumbnails, edit rates — same
 * flows as Dashboard → update rates → Giftcard, without crypto/point tabs.
 */
const GiftcardRatesAdminPanel: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const cardId = searchParams.get("card") ?? "";

  const [cardModal, setCardModal] = useState(false);
  const [uploadCardImage, setUploadCardImage] = useState(false);
  const [addGiftcard, setAddGiftcard] = useState(false);

  const invalidateCatalog = useInvalidateGiftCardCatalog();

  const setCatalogCard = useCallback(
    (id: string) => {
      const p = new URLSearchParams(searchParams);
      p.set("tab", "catalog");
      if (id) {
        p.set("card", id);
      } else {
        p.delete("card");
      }
      setSearchParams(p, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const { data: allGiftcards = [] } = useGiftCardsListQuery("all", {
    enabled: true,
  });

  const {
    data: cardDetails = null,
    isPending: detailLoading,
    isError: detailIsError,
    refetch: refetchDetail,
  } = useGiftCardDetailQuery(cardId, { enabled: Boolean(cardId) });

  return (
    <div className="w-full overflow-hidden rounded-xl border border-[#2C363E] bg-[#13181B]">
      <div className="border-b border-[#2C363E] bg-[#161E25]/80 px-4 py-4 sm:px-6">
        <h2 className="font-sora text-[16px] font-semibold text-white">
          Catalog &amp; rates
        </h2>
        <p className="mt-1 max-w-3xl text-[13px] leading-relaxed text-[#869AA9]">
          Choose a card to edit rates and visibility. Use{" "}
          <span className="font-semibold text-[#78C2F7]">Choose card</span> to open
          the picker, or the{" "}
          <span className="font-semibold text-[#78C2F7]">+</span> tile to create a
          new card and thumbnail. Add country and receipt-specific rows from{" "}
          <span className="font-semibold text-white">Add New Rate</span>.
        </p>
      </div>

      {!cardId ? (
        <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
          <p className="text-[15px] font-semibold text-white font-sora">
            No card selected
          </p>
          <p className="mt-2 max-w-md text-[13px] leading-relaxed text-[#869AA9]">
            Pick a gift card from the catalog to view and edit its rates. You can
            bookmark or share this page; the selected card is kept in the URL.
          </p>
          <button
            type="button"
            onClick={() => setCardModal(true)}
            className="mt-6 rounded-xl bg-secondary_button px-6 py-3 text-[14px] font-semibold text-white shadow-sm transition hover:opacity-95 active:scale-[0.98]"
          >
            Choose card
          </button>
        </div>
      ) : (
        <div className="min-h-0 p-2 sm:p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2 px-2">
            <button
              type="button"
              onClick={() => setCardModal(true)}
              className="rounded-lg border border-[#2C363E] bg-[#161E25] px-3 py-2 text-[12px] font-semibold text-[#AEBCC6] transition hover:border-[#3A4852] hover:text-white"
            >
              Change card
            </button>
            <button
              type="button"
              onClick={() => setCatalogCard("")}
              className="rounded-lg border border-[#2C363E] px-3 py-2 text-[12px] font-semibold text-[#869AA9] transition hover:border-[#3A4852] hover:text-white"
            >
              Clear selection
            </button>
          </div>
          <GiftcardRateFlow
            setCardModal={setCardModal}
            cardId={cardId}
            allGiftcards={allGiftcards}
            cardDetails={cardDetails}
            detailLoading={detailLoading}
            detailError={detailIsError}
            onRetryDetail={() => void refetchDetail()}
            setAddGiftcard={setAddGiftcard}
          />
        </div>
      )}

      {cardModal ? (
        <CardModal
          cardId={cardId}
          onSelectCard={setCatalogCard}
          setUploadCardImage={setUploadCardImage}
          setCardModal={setCardModal}
        />
      ) : null}
      {uploadCardImage ? (
        <UploadCardImage
          setUploadCardImage={setUploadCardImage}
          setCardModal={setCardModal}
          onCatalogChanged={() => invalidateCatalog()}
        />
      ) : null}
      {addGiftcard ? (
        <ExpandAdd setAddGiftcard={setAddGiftcard} cardDetails={cardDetails} />
      ) : null}
    </div>
  );
};

export default GiftcardRatesAdminPanel;
